import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { productsAPI } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { ShoppingCart, Heart } from 'lucide-react';
import { updatePageSEO, seoTemplates, addBreadcrumbStructuredData } from '../utils/seo';

// Resim URL'sini dinamik olarak oluştur
const getImageUrl = (imagePath) => {
  // Resim yolu kontrolü
  if (!imagePath || imagePath === undefined || imagePath === null) {
    return '/placeholder-image.jpg';
  }
  
  // Eğer object ise url property'sini al
  let actualPath = imagePath;
  if (typeof imagePath === 'object' && imagePath.url) {
    actualPath = imagePath.url;
  } else if (typeof imagePath === 'string') {
    actualPath = imagePath;
  } else {
    return '/placeholder-image.jpg';
  }
  
  // Production'da HTTPS kullan
  if (process.env.NODE_ENV === 'production') {
    // HTTPS ile başlamıyorsa HTTPS ekle
    if (actualPath.startsWith('http://')) {
      return actualPath.replace('http://', 'https://');
    }
    // HTTPS ile başlıyorsa direkt döndür
    if (actualPath.startsWith('https://')) {
      return actualPath;
    }
    // Relative path ise HTTPS domain ile birleştir
    if (actualPath.startsWith('/')) {
      return `https://leasertech.com${actualPath}`;
    }
    return `https://leasertech.com/${actualPath}`;
  }
  
  // Development'ta localhost kullan
  const API_BASE_URL = 'http://localhost:5000';
  
  // Eğer dış kaynak URL ise proxy kullan
  if (actualPath.startsWith('http://') || actualPath.startsWith('https://')) {
    const proxyUrl = `${API_BASE_URL}/proxy-image?url=${encodeURIComponent(actualPath)}`;
    return proxyUrl;
  }
  
  // Yerel resim için normal yol
  const cleanedPath = actualPath.startsWith('/') ? actualPath : `/${actualPath}`;
  const finalUrl = `${API_BASE_URL}${cleanedPath}`;
  
  return finalUrl;
};

// Skeleton Loading Component
const ProductSkeleton = () => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
    <div className="bg-gray-200 h-48 w-full"></div>
    <div className="p-4">
      <div className="bg-gray-200 h-4 rounded mb-2"></div>
      <div className="bg-gray-200 h-4 rounded w-3/4 mb-2"></div>
      <div className="bg-gray-200 h-6 rounded w-1/2 mb-3"></div>
      <div className="flex justify-between items-center">
        <div className="bg-gray-200 h-8 w-20 rounded"></div>
        <div className="bg-gray-200 h-8 w-8 rounded"></div>
      </div>
    </div>
  </div>
);

const ProductsSkeleton = () => (
  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
    {[...Array(8)].map((_, index) => (
      <ProductSkeleton key={index} />
    ))}
  </div>
);

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(new Set());
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [touchStart, setTouchStart] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0,
    hasNext: false,
    hasPrev: false
  });
  
  const { addToCart, loading: cartLoading } = useCart();
  const { isAuthenticated } = useAuth();
  
  // URL parametrelerini dinamik olarak oku
  const filters = {
    category: searchParams.get('category') || '',
    search: searchParams.get('search') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    sort: searchParams.get('sort') || 'newest',
    stockStatus: searchParams.get('stockStatus') || ''
  };

  useEffect(() => {
    loadProducts();
    
    // SEO Setup based on current filters
    let seoData;
    if (filters.category) {
      seoData = seoTemplates.category(filters.category);
    } else if (filters.search) {
      seoData = seoTemplates.search(filters.search);
    } else {
      seoData = seoTemplates.products;
    }
    
    updatePageSEO(
      seoData.title,
      seoData.description,
      seoData.keywords,
      'https://leasertech.com/og-image.jpg',
      `https://leasertech.com/products${window.location.search}`
    );
    
    // Add breadcrumb structured data
    const breadcrumbs = [
      { name: 'Ana Sayfa', url: 'https://leasertech.com/' },
      { name: filters.category || 'Ürünler', url: `https://leasertech.com/products${window.location.search}` }
    ];
    addBreadcrumbStructuredData(breadcrumbs);
    
  }, [searchParams, filters.category, filters.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sayfa yüklendiğinde scroll'u en üste al
  useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      loadFavorites();
    }
  }, [isAuthenticated]);

  const handleAddToCart = async (productId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Artık misafir kullanıcılar da sepete ekleyebilir
    await addToCart(productId, 1);
  };

  const handleToggleFavorite = async (productId, e) => {
    console.log('handleToggleFavorite called with productId:', productId);
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      console.log('User not authenticated, redirecting to login');
      window.location.href = '/login';
      return;
    }

    try {
      console.log('Current favorites:', [...favorites]);
      // Mock favori işlemi - state ve localStorage'ı güncelle
      if (favorites.has(productId)) {
        // Favorilerden çıkar
        setFavorites(prev => {
          const newFavorites = new Set(prev);
          newFavorites.delete(productId);
          localStorage.setItem('favorites', JSON.stringify([...newFavorites]));
          console.log('Ürün favorilerden çıkarıldı, new favorites:', [...newFavorites]);
          return newFavorites;
        });
      } else {
        // Favorilere ekle
        setFavorites(prev => {
          const newFavorites = new Set(prev);
          newFavorites.add(productId);
          localStorage.setItem('favorites', JSON.stringify([...newFavorites]));
          console.log('Ürün favorilere eklendi, new favorites:', [...newFavorites]);
          return newFavorites;
        });
      }
    } catch (error) {
      console.error('Favori işlemi hatası:', error);
    }
  };

  const loadFavorites = async () => {
    try {
      // Mock favoriler - localStorage'dan yükle
      const savedFavorites = localStorage.getItem('favorites');
      if (savedFavorites) {
        const favoriteIds = new Set(JSON.parse(savedFavorites));
        setFavorites(favoriteIds);
      }
    } catch (error) {
      console.error('Favoriler yüklenemedi:', error);
    }
  };

  const handleImageHover = (productId, direction) => {
    const product = products.find(p => p.id === productId);
    
    if (!product || !product.images || product.images.length <= 1) {
      return;
    }

    const currentIndex = currentImageIndex[productId] || 0;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % product.images.length;
    } else {
      newIndex = currentIndex === 0 ? product.images.length - 1 : currentIndex - 1;
    }

    // Sadece index değiştiyse güncelle
    if (newIndex !== currentIndex) {
      setCurrentImageIndex(prev => ({
        ...prev,
        [productId]: newIndex
      }));
    }
  };

  // Touch events for mobile image swiping
  const handleTouchStart = (e, productId) => {
    console.log('Products.js Touch Start - ProductId:', productId);
    console.log('Products array length:', products.length);
    console.log('Products IDs:', products.map(p => p.id));
    
    const product = products.find(p => p.id === productId);
    console.log('Found product:', product);
    
    if (!product) {
      console.log('Product not found in products array:', productId);
      return;
    }
    
    if (!product.images || product.images.length <= 1) {
      console.log('Touch start ignored - single image product:', productId);
      return;
    }
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    console.log('Touch start:', productId, clientX, 'images:', product.images.length);
    setTouchStart({ x: clientX, y: clientY, productId });
  };

  const handleTouchEnd = (e, productId) => {
    if (!touchStart || touchStart.productId !== productId) return;
    
    const clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const deltaX = clientX - touchStart.x;
    
    console.log('Touch end:', productId, deltaX, Math.abs(deltaX));
    
    // Simple horizontal swipe detection
    if (Math.abs(deltaX) > 30) {
      const product = products.find(p => p.id === productId);
      
      if (!product || !product.images || product.images.length <= 1) {
        setTouchStart(null);
        return;
      }

      const currentIndex = currentImageIndex[productId] || 0;
      let newIndex;
      
      if (deltaX > 0) {
        // Swipe right - previous image
        newIndex = currentIndex === 0 ? product.images.length - 1 : currentIndex - 1;
      } else {
        // Swipe left - next image
        newIndex = (currentIndex + 1) % product.images.length;
      }

      console.log('Changing image from', currentIndex, 'to', newIndex);
      console.log('Product images:', product.images.length);
      setCurrentImageIndex(prev => {
        console.log('Setting new index:', { ...prev, [productId]: newIndex });
        return { ...prev, [productId]: newIndex };
      });
    }
    
    setTouchStart(null);
  };

  const loadProducts = async (retryCount = 0) => {
    try {
      setLoading(true);
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.search) params.search = filters.search;
      if (filters.minPrice) params.minPrice = filters.minPrice;
      if (filters.maxPrice) params.maxPrice = filters.maxPrice;
      if (filters.sort) params.sort = filters.sort;
      if (filters.stockStatus) params.stockStatus = filters.stockStatus;
      
      // Pagination parametresi
      const page = searchParams.get('page') || '1';
      params.page = page;

      const response = await productsAPI.getProducts(params);
      setProducts(response.data.products);
      setPagination(response.data.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalProducts: response.data.products.length,
        hasNext: false,
        hasPrev: false
      });
    } catch (error) {
      console.error('Ürünler yüklenemedi:', error);
      
      // 429 hatası durumunda retry
      if (error.response?.status === 429 && retryCount < 2) {
        const delay = (retryCount + 1) * 2000; // 2s, 4s
        console.log(`Retrying products, attempt ${retryCount + 1} after ${delay}ms`);
        setTimeout(() => loadProducts(retryCount + 1), delay);
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    'Şarj Aleti',
    'Powerbank', 
    'Kulaklık',
    'Hoparlör',
    'Otomobil Ürünleri',
    'Telefon Tutacakları',
    'Projeksiyon Cihazları',
    'Bilgisayar Aksesuarları',
    'Telefon Aksesuarları',
    'Diğer'
  ];

  return (
    <div className="min-h-screen bg-orange-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {filters.search
            ? `"${filters.search}" Arama Sonuçları`
            : filters.category || 'Tüm Ürünler'}
        </h1>
        {(filters.category || filters.search) && (
          <p className="text-gray-600 mb-8 max-w-3xl leading-relaxed">
            {filters.category
              ? seoTemplates.getCategoryIntro(filters.category)
              : seoTemplates.getSearchIntro(filters.search)}
          </p>
        )}
        {!filters.category && !filters.search && (
          <p className="text-gray-600 mb-8 max-w-3xl leading-relaxed">
            Bluetooth kulaklık, powerbank, ses bombası, bluetooth hoparlör ve projeksiyon cihazları
            dahil tüm elektronik aksesuarları keşfedin.
          </p>
        )}
        
        {/* Profesyonel Filtreler */}
        <div className="mb-8">
          {/* Ana Filtre Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              
              {/* Sol Taraf - Kategori ve Stok */}
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                {/* Kategori */}
                <div className="relative">
                  <select
                    value={filters.category || ''}
                    onChange={(e) => {
                      const url = new URL(window.location);
                      if (e.target.value) {
                        url.searchParams.set('category', e.target.value);
                      } else {
                        url.searchParams.delete('category');
                      }
                      window.location.href = url.toString();
                    }}
                    className="appearance-none bg-gray-50 border-0 rounded-lg px-4 py-3 pr-10 text-sm font-medium text-gray-700 focus:ring-2 focus:ring-orange-500 focus:bg-white transition-all duration-200 w-full sm:w-auto min-w-[160px]"
                  >
                    <option value="">📦 Tüm Kategoriler</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Stok Durumu */}
                <div className="relative">
                  <select
                    value={filters.stockStatus || ''}
                    onChange={(e) => {
                      const url = new URL(window.location);
                      if (e.target.value) {
                        url.searchParams.set('stockStatus', e.target.value);
                      } else {
                        url.searchParams.delete('stockStatus');
                      }
                      window.location.href = url.toString();
                    }}
                    className="appearance-none bg-gray-50 border-0 rounded-lg px-4 py-3 pr-10 text-sm font-medium text-gray-700 focus:ring-2 focus:ring-orange-500 focus:bg-white transition-all duration-200 w-full sm:w-auto min-w-[140px]"
                  >
                    <option value="">📦 Stok Durumu</option>
                    <option value="inStock">✅ Stokta Var</option>
                    <option value="lowStock">⚠️ Az Stokta</option>
                    <option value="outOfStock">❌ Stokta Yok</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Sağ Taraf - Sıralama ve Fiyat */}
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Sıralama */}
                <div className="relative">
                  <select
                    value={filters.sort || 'newest'}
                    onChange={(e) => {
                      const url = new URL(window.location);
                      url.searchParams.set('sort', e.target.value);
                      window.location.href = url.toString();
                    }}
                    className="appearance-none bg-gray-50 border-0 rounded-lg px-4 py-3 pr-10 text-sm font-medium text-gray-700 focus:ring-2 focus:ring-orange-500 focus:bg-white transition-all duration-200 w-full sm:w-auto min-w-[160px]"
                  >
                    <option value="newest">🕒 En Yeni</option>
                    <option value="price-low">💰 Fiyat (Düşük → Yüksek)</option>
                    <option value="price-high">💰 Fiyat (Yüksek → Düşük)</option>
                    <option value="name">🔤 İsim (A → Z)</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Fiyat Aralığı */}
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min ₺"
                    value={filters.minPrice || ''}
                    onChange={(e) => {
                      const url = new URL(window.location);
                      if (e.target.value) {
                        url.searchParams.set('minPrice', e.target.value);
                      } else {
                        url.searchParams.delete('minPrice');
                      }
                      window.location.href = url.toString();
                    }}
                    className="w-20 px-3 py-3 bg-gray-50 border-0 rounded-lg text-sm font-medium text-gray-700 focus:ring-2 focus:ring-orange-500 focus:bg-white transition-all duration-200 text-center placeholder-gray-400"
                  />
                  <span className="flex items-center text-gray-400 font-medium">-</span>
                  <input
                    type="number"
                    placeholder="Max ₺"
                    value={filters.maxPrice || ''}
                    onChange={(e) => {
                      const url = new URL(window.location);
                      if (e.target.value) {
                        url.searchParams.set('maxPrice', e.target.value);
                      } else {
                        url.searchParams.delete('maxPrice');
                      }
                      window.location.href = url.toString();
                    }}
                    className="w-20 px-3 py-3 bg-gray-50 border-0 rounded-lg text-sm font-medium text-gray-700 focus:ring-2 focus:ring-orange-500 focus:bg-white transition-all duration-200 text-center placeholder-gray-400"
                  />
                </div>
              </div>
            </div>

            {/* Aktif Filtreler */}
            {(filters.category || filters.stockStatus || filters.minPrice || filters.maxPrice) && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Aktif Filtreler</span>
                    <div className="flex flex-wrap gap-2">
                      {filters.category && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                          <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                          {filters.category}
                        </span>
                      )}
                      {filters.stockStatus && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          {filters.stockStatus === 'inStock' ? 'Stokta Var' : 
                           filters.stockStatus === 'lowStock' ? 'Az Stokta' : 'Stokta Yok'}
                        </span>
                      )}
                      {filters.minPrice && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          Min: ₺{filters.minPrice}
                        </span>
                      )}
                      {filters.maxPrice && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          Max: ₺{filters.maxPrice}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      const url = new URL(window.location);
                      url.searchParams.delete('category');
                      url.searchParams.delete('stockStatus');
                      url.searchParams.delete('minPrice');
                      url.searchParams.delete('maxPrice');
                      window.location.href = url.toString();
                    }}
                    className="text-xs text-gray-400 hover:text-red-500 transition-colors font-medium"
                  >
                    ✕ Temizle
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Ürün Sayısı Bilgisi */}
        {!loading && products.length > 0 && (
          <div className="mb-6 text-center">
            <p className="text-gray-600">
              <span className="font-semibold text-orange-600">{pagination.totalProducts}</span> ürün bulundu
              {pagination.totalPages > 1 && (
                <span className="text-gray-500"> - Sayfa {pagination.currentPage} / {pagination.totalPages}</span>
              )}
            </p>
          </div>
        )}

        {loading ? (
          <ProductsSkeleton />
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg">Ürün bulunamadı</div>
            <div className="text-gray-400 text-sm mt-2">
              Farklı filtreler deneyebilirsiniz
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
            {products.map((product) => (
              <div 
                key={product.id} 
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow flex flex-col h-full"
                onMouseLeave={() => setCurrentImageIndex(prev => ({
                  ...prev,
                  [product.id]: 0
                }))}
              >
                <Link
                  to={`/product/${product.id}`}
                  className="block flex-1 flex flex-col"
                >
                <div className="relative bg-gray-100 group">
                  {/* Favori İkonu */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleToggleFavorite(product.id, e);
                    }}
                    className="absolute top-2 right-2 z-30 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 group/favorite cursor-pointer"
                    title="Favorilere Ekle"
                  >
                    <Heart className={`h-4 w-4 transition-colors ${
                      favorites.has(product.id) 
                        ? 'text-red-500 fill-red-500' 
                        : 'text-gray-400 group-hover/favorite:text-red-500'
                    }`} />
                  </button>

                  {/* Görsel Kaydırma Alanları - Trendyol Tarzı */}
                  {product.images && product.images.length > 1 && (
                    <>
                      <div
                        onMouseEnter={() => handleImageHover(product.id, 'prev')}
                        className="absolute left-0 top-0 w-1/2 h-full z-10 cursor-pointer"
                        title="Önceki Görsel"
                      />
                      <div
                        onMouseEnter={() => handleImageHover(product.id, 'next')}
                        className="absolute right-0 top-0 w-1/2 h-full z-10 cursor-pointer"
                        title="Sonraki Görsel"
                      />
                    </>
                  )}
                  
                  {product.images && product.images.length > 0 ? (
                    <div 
                      className={`aspect-square bg-white relative overflow-hidden ${product.images && product.images.length > 1 ? 'cursor-pointer' : ''}`}
                      onTouchStart={(e) => handleTouchStart(e, product.id)}
                      onTouchMove={(e) => {
                        // Only prevent default for horizontal swipes on multi-image products
                        if (touchStart && touchStart.productId === product.id && product.images && product.images.length > 1) {
                          const touch = e.touches[0];
                          const deltaX = Math.abs(touch.clientX - touchStart.x);
                          const deltaY = Math.abs(touch.clientY - touchStart.y);
                          if (deltaX > deltaY && deltaX > 10) {
                            e.preventDefault();
                          }
                        }
                      }}
                      onTouchEnd={(e) => handleTouchEnd(e, product.id)}
                      onMouseDown={(e) => handleTouchStart(e, product.id)}
                      onMouseUp={(e) => handleTouchEnd(e, product.id)}
                      onClick={(e) => {
                        e.preventDefault();
                        // Only allow click to change image if product has multiple images
                        if (product.images && product.images.length > 1) {
                          console.log('CLICK DETECTED!', product.id, product.images.length);
                          // Click ile de resim değiştir
                          const currentIndex = currentImageIndex[product.id] || 0;
                          const newIndex = (currentIndex + 1) % product.images.length;
                          console.log('Changing from', currentIndex, 'to', newIndex);
                          setCurrentImageIndex(prev => ({
                            ...prev,
                            [product.id]: newIndex
                          }));
                        }
                      }}
                    >
                      <img
                        src={getImageUrl(product.images[currentImageIndex[product.id] || 0]?.url || product.images[0].url)}
                        alt={`${product.name} - ${product.brand || ''} - ${product.categories?.join(', ') || ''} - LeaserTech`}
                        title={product.name}
                        className="w-full h-full object-contain p-4 hover:scale-105 transition-all duration-300 ease-in-out"
                        crossOrigin="anonymous"
                        loading="lazy"
                      />
                      
                      {/* Görsel İndikatörleri - Görseldeki gibi */}
                      {product.images.length > 1 && (
                        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1 z-20">
                          {product.images.map((_, index) => (
                            <div
                              key={index}
                              className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                                (currentImageIndex[product.id] || 0) === index
                                  ? 'bg-gray-800'
                                  : 'bg-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="aspect-square bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-400 text-sm">Resim Yok</span>
                    </div>
                  )}
                </div>
                <div className="p-3 sm:p-4 flex-1 flex flex-col">
                  <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 line-clamp-2 text-sm sm:text-base">
                    {product.name}
                  </h3>
                  <p className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">
                    ₺{product.price.toLocaleString()}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500 mb-1 sm:mb-2">
                    {product.brand}
                  </p>
                  
                  {/* Stok Durumu Göstergesi */}
                  <div className="mb-2">
                    {product.stock > 10 ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                        Stokta Var
                      </span>
                    ) : product.stock > 0 ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full mr-1"></div>
                        Az Stokta ({product.stock})
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <div className="w-2 h-2 bg-red-500 rounded-full mr-1"></div>
                        Stokta Yok
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 mb-3 flex-1">
                    <p className="whitespace-pre-line line-clamp-3">
                      {product.description}
                    </p>
                  </div>
                </div>
                </Link>
                
                {/* Action Buttons */}
                <div className="flex gap-2 p-3 sm:p-4 pt-0 mt-auto">
                  <button
                    onClick={(e) => handleAddToCart(product.id, e)}
                    disabled={cartLoading || product.stock === 0}
                    className={`w-full flex items-center justify-center gap-1 py-2 px-2 sm:px-3 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                      product.stock > 0 && !cartLoading
                        ? 'bg-orange-500 text-white hover:bg-orange-600'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <ShoppingCart className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">{cartLoading ? '...' : 'Sepete Ekle'}</span>
                    <span className="sm:hidden">{cartLoading ? '...' : 'Ekle'}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2 mt-8">
            <button
              onClick={() => {
                if (pagination.hasPrev) {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set('page', pagination.currentPage - 1);
                  setSearchParams(newParams);
                }
              }}
              disabled={!pagination.hasPrev}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                pagination.hasPrev
                  ? 'bg-orange-500 text-white hover:bg-orange-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Önceki
            </button>
            
            <span className="px-4 py-2 text-gray-600 font-medium">
              Sayfa {pagination.currentPage} / {pagination.totalPages}
            </span>
            
            <button
              onClick={() => {
                if (pagination.hasNext) {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set('page', pagination.currentPage + 1);
                  setSearchParams(newParams);
                }
              }}
              disabled={!pagination.hasNext}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                pagination.hasNext
                  ? 'bg-orange-500 text-white hover:bg-orange-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Sonraki
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;

